import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Search, FileText, Filter, ChevronRight,
  MoreVertical, Send, Download, CheckCircle2, Clock, AlertCircle
} from 'lucide-react';
import '../styles/mobile.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount, currency = 'SEK') => {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function MobileInvoices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const token = localStorage.getItem('token');

  const fetchInvoices = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const filteredInvoices = invoices
    .filter(inv => filter === 'all' || inv.status === filter)
    .filter(inv => 
      !searchQuery || 
      inv.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.invoice_number?.toString().includes(searchQuery)
    );

  const stats = {
    all: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'paid': return <CheckCircle2 size={16} />;
      case 'sent': return <Send size={16} />;
      case 'overdue': return <AlertCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      paid: { class: 'success', label: t('invoices.status.paid') },
      sent: { class: 'info', label: t('invoices.status.sent') },
      overdue: { class: 'danger', label: t('invoices.status.overdue') },
      draft: { class: 'neutral', label: t('invoices.status.draft') }
    };
    const s = statusMap[status] || statusMap.draft;
    return (
      <span className={`mobile-badge ${s.class}`}>
        {getStatusIcon(status)}
        <span style={{ marginLeft: '4px' }}>{s.label}</span>
      </span>
    );
  };

  return (
    <div className="mobile-app">
      {/* Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="mobile-header-btn" onClick={() => navigate('/m')}>
            <ArrowLeft size={20} />
          </button>
          <div className="mobile-header-title">{t('invoices.title')}</div>
        </div>
        <button className="mobile-header-btn" onClick={() => navigate('/m/invoices/new')}>
          <Plus size={20} />
        </button>
      </header>

      {/* Main Content */}
      <main className="mobile-content">
        {/* Search */}
        <div className="mobile-search">
          <Search size={18} className="mobile-search-icon" />
          <input
            type="text"
            className="mobile-search-input"
            placeholder={`${t('common.search')}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
          {[
            { key: 'all', label: t('common.all'), count: stats.all },
            { key: 'draft', label: t('invoices.status.draft'), count: stats.draft },
            { key: 'sent', label: t('invoices.status.sent'), count: stats.sent },
            { key: 'paid', label: t('invoices.status.paid'), count: stats.paid },
            { key: 'overdue', label: t('invoices.status.overdue'), count: stats.overdue },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                background: filter === tab.key ? '#1e3a5f' : '#f1f5f9',
                color: filter === tab.key ? 'white' : '#64748b',
                fontWeight: 500,
                fontSize: '13px',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Invoice List */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="mobile-spinner" />
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="mobile-empty">
            <div className="mobile-empty-icon"><FileText size={48} /></div>
            <div className="mobile-empty-title">{t('dashboard.noInvoices')}</div>
            <div className="mobile-empty-desc">{t('invoices.newInvoice')}</div>
            <button 
              className="mobile-btn mobile-btn-primary" 
              style={{ marginTop: '16px', width: 'auto', padding: '12px 24px' }}
              onClick={() => navigate('/m/invoices/new')}
            >
              <Plus size={18} />
              {t('invoices.createInvoice')}
            </button>
          </div>
        ) : (
          <div className="mobile-list">
            {filteredInvoices.map((invoice, idx) => (
              <div 
                key={idx} 
                className="mobile-list-item"
                onClick={() => navigate(`/m/invoices/${invoice.id}`)}
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                <div 
                  className="mobile-list-icon" 
                  style={{ 
                    background: invoice.status === 'paid' ? '#dcfce7' : 
                               invoice.status === 'overdue' ? '#fee2e2' : '#e0f2fe',
                    color: invoice.status === 'paid' ? '#16a34a' : 
                          invoice.status === 'overdue' ? '#dc2626' : '#0284c7'
                  }}
                >
                  <FileText size={20} />
                </div>
                <div className="mobile-list-content">
                  <div className="mobile-list-title">{invoice.customer_name || 'Kund'}</div>
                  <div className="mobile-list-subtitle">
                    #{invoice.invoice_number} • {formatDate(invoice.invoice_date)}
                  </div>
                </div>
                <div className="mobile-list-value">
                  <div className="mobile-list-amount" style={{ marginBottom: '4px' }}>
                    {formatCurrency(invoice.total)}
                  </div>
                  {getStatusBadge(invoice.status)}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* FAB */}
      <button className="mobile-fab" onClick={() => navigate('/m/invoices/new')}>
        <Plus size={24} />
      </button>
    </div>
  );
}
